import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Star, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASPECT_LABELS = {
  puntualidad: 'Puntualidad',
  profesionalidad: 'Profesionalidad / técnica',
  comunicacion: 'Comunicación',
  actitud: 'Actitud',
  condiciones_pago: 'Condiciones y pago',
  organizacion: 'Organización',
  trato: 'Trato',
  calidad_equipos: 'Calidad de los equipos',
};

const StarRow = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" data-testid={`${testId}-${n}`} onClick={() => onChange(n)} className="p-0.5">
        <Star size={22} className={n <= value ? 'text-amber-500 fill-amber-500' : 'text-slate-600'} strokeWidth={1.5} />
      </button>
    ))}
  </div>
);

export const PendingReviews = () => {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [penaltyUntil, setPenaltyUntil] = useState(null);
  const [idx, setIdx] = useState(0);
  const [overall, setOverall] = useState(0);
  const [aspects, setAspects] = useState({});
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!user) { setPending([]); setOpen(false); return; }
    fetch(`${API}/reviews/pending`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { pending: [], penalty_until: null }))
      .then((data) => {
        setPending(data.pending || []);
        setPenaltyUntil(data.penalty_until || null);
        setOpen((data.pending || []).length > 0);
        setIdx(0);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const current = pending[idx];
  useEffect(() => { setOverall(0); setAspects({}); setComment(''); }, [idx, current?.id]);

  if (!user || !open || !current) return null;

  const submit = async () => {
    if (!overall) { toast.error('Añade la valoración global (1-5 estrellas)'); return; }
    for (const a of current.aspects) {
      if (!aspects[a]) { toast.error(`Puntúa: ${ASPECT_LABELS[a] || a}`); return; }
    }
    if (!comment.trim()) { toast.error('El comentario es obligatorio'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/reviews/pending/${current.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ overall, aspects, comment: comment.trim() }),
      });
      if (!res.ok) throw new Error();
      toast.success('Valoración enviada');
      const rest = pending.filter((_, i) => i !== idx);
      setPending(rest);
      if (rest.length === 0) { setOpen(false); } else { setIdx(0); }
    } catch {
      toast.error('No se pudo enviar la valoración.');
    } finally { setSaving(false); }
  };

  const dirLabel = current.direction === 'company_to_pro' ? 'al profesional' : 'a la empresa';

  return (
    <Dialog open={open} onOpenChange={() => { /* mandatory: cannot dismiss */ }}>
      <DialogContent data-testid="pending-review-modal" className="bg-[#121418] border-[#2A2F3D] text-white max-w-lg max-h-[90dvh] overflow-y-auto p-0 gap-0" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
        <div className="p-6 border-b border-[#2A2F3D]">
          <DialogTitle className="text-xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Valoración obligatoria</DialogTitle>
          <DialogDescription className="text-sm text-slate-400 mt-1">Valora {dirLabel} <span className="text-white font-semibold">{current.subject_name}</span> del trabajo «{current.job_title}». {pending.length > 1 && <span className="text-amber-500">({pending.length} pendientes)</span>}</DialogDescription>
        </div>
        <div className="p-6 space-y-5">
          {penaltyUntil && (
            <div className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/5 px-4 py-3" data-testid="review-penalty-banner">
              <ShieldAlert size={18} className="text-red-400 shrink-0 mt-0.5" />
              <span className="text-sm text-red-300">Tienes una restricción activa: no puedes coger ni publicar ofertas durante 1 semana por no valorar a tiempo. Completa tus valoraciones.</span>
            </div>
          )}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Valoración global *</label>
            <StarRow value={overall} onChange={setOverall} testId="review-overall" />
          </div>
          <div className="space-y-3">
            {current.aspects.map((a) => (
              <div key={a} className="flex items-center justify-between gap-3">
                <span className="text-sm text-slate-300">{ASPECT_LABELS[a] || a} *</span>
                <StarRow value={aspects[a] || 0} onChange={(v) => setAspects((p) => ({ ...p, [a]: v }))} testId={`review-aspect-${a}`} />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Comentario *</label>
            <textarea data-testid="review-comment" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Cuenta cómo fue la experiencia de trabajo…"
              className="w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white text-sm resize-none" />
          </div>
          <button data-testid="review-submit" onClick={submit} disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors">
            {saving ? 'Enviando…' : 'Enviar valoración'}
          </button>
          <p className="text-[11px] text-slate-500 text-center">Si no valoras en 7 días desde la finalización, no podrás coger ni publicar ofertas durante 1 semana.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PendingReviews;
